
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CheckCircle, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../hooks/useSubscription";
import { PayPalButton } from "./PayPalButton";
import { PlatformAwarePaymentButton } from "./PlatformAwarePaymentButton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { useTranslation } from 'react-i18next';
import { revisePaypalSubscription, getPaypalPlanIdForTier } from "../services/paypalService";
import { toast } from "@/hooks/use-toast";
import { usePaymentPlatform } from "../contexts/PaymentPlatformContext";

// ✅ Updated helper function with standardized features
const getTierFeatures = (tier: string, t: any) => {
  const baseFeatures = {
    free: [
      "Up to 2 pets",
      "100 MB total storage",
      "Photo & video uploads up to storage limit",
      "Public pet profiles",
      "15-day premium trial with unlimited features:",
      "• Unlimited medical records during trial",
      "• After trial: Read-only access to medical records",
      "• PetMatch™ - 6 free credits (Find playmates, friends, partners or a new home)",
      "• Chat with users who accept your PetMatch™ request",
      "• Unlimited search for veterinarians, veterinary clinics, pet stores, groomers & dog walkers"
    ],
    standard: [
      "Up to 3 pets",
      "1 GB total storage",
      "Photo & video uploads up to storage limit",
      "Public pet profiles",
      "Sharing to Social Media",
      "Up to 5 medical records per pet",
      "Medical prescription uploads",
      "Appointment & vaccination tracking",
      "PetMatch™ - 6 free credits (Find playmates, friends, partners or a new home)",
      "Chat with users who accept your PetMatch™ request",
      "Unlimited search for veterinarians, veterinary clinics, pet stores, groomers & dog walkers"
    ],
    premium: [
      "Up to 5 pets",
      "3 GB total storage",
      "Photo & video uploads up to storage limit",
      "Public pet profiles",
      "Sharing to Social Media",
      "Up to 10 medical records per pet",
      "Medical prescription uploads",
      "Appointment & vaccination tracking",
      "PetMatch™ - 6 free credits (Find playmates, friends, partners or a new home)",
      "Chat with users who accept your PetMatch™ request",
      "Unlimited search for veterinarians, veterinary clinics, pet stores, groomers & dog walkers"
    ],
    ultimate: [
      "Up to 15 pets",
      "10 GB total storage",
      "Photo & video uploads up to storage limit",
      "Public pet profiles",
      "Sharing to Social Media",
      "Up to 20 medical records per pet",
      "Complete medical records",
      "Advanced medical tracking",
      "Medical prescription uploads",
      "Complete appointment & vaccination system",
      "Priority customer support",
      "PetMatch™ - 6 free credits (Find playmates, friends, partners or a new home)",
      "Chat with users who accept your PetMatch™ request",
      "Unlimited search for veterinarians, veterinary clinics, pet stores, groomers & dog walkers"
    ]
  };

  // Try to use translations if available, fallback to base features
  try {
    const translatedFeatures = t(`plans.${tier}.features`, { returnObjects: true });
    return Array.isArray(translatedFeatures) ? translatedFeatures : baseFeatures[tier as keyof typeof baseFeatures];
  } catch (error) {
    return baseFeatures[tier as keyof typeof baseFeatures] || baseFeatures.free;
  }
};

// Helper function to get tier pricing (always in USD)
// Supports both web (PayPal) and native (Apple/Google) prices
const getTierPrice = (tier: string, billingCycle: 'monthly' | 'annual', isNative: boolean = false) => {
  // Web prices (PayPal)
  const webPrices = {
    free: { monthly: "Free", annual: "Free" },
    standard: { monthly: "$6.99/month", annual: "$76.99/year" },
    premium: { monthly: "$9.99/month", annual: "$109.89/year" },
    ultimate: { monthly: "$19.99/month", annual: "$219.89/year" }
  };

  // Native prices (Apple/Google stores) - Official store prices
  const nativePrices = {
    free: { monthly: "Free", annual: "Free" },
    standard: { monthly: "$11.99/month", annual: "$98.99/year" },
    premium: { monthly: "$14.99/month", annual: "$164.99/year" },
    ultimate: { monthly: "$28.99/month", annual: "$319.99/year" }
  };

  const prices = isNative ? nativePrices : webPrices;
  return prices[tier as keyof typeof prices]?.[billingCycle] || "Free";
};

export const SubscriptionPlans = () => {
  const { subscription, isAnyLoading } = useSubscription();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [changingPlan, setChangingPlan] = useState(false);
  const { t } = useTranslation();
  const { isNative, paymentMethod } = usePaymentPlatform();

  const tiers: ('free' | 'standard' | 'premium' | 'ultimate')[] = ["free", "standard", "premium", "ultimate"];

  const handleChangePlan = async (newTier: 'standard' | 'premium' | 'ultimate') => {
    try {
      setChangingPlan(true);

      // Get current subscription ID from the subscription data
      const currentSubscriptionId = subscription?.data?.paypal_subscription_id;

      if (!currentSubscriptionId) {
        toast({
          title: t('common.error'),
          description: t('subscription.noActivePayPal'),
          variant: "destructive",
        });
        return;
      }

      // Get the new plan ID
      const newPlanId = getPaypalPlanIdForTier(newTier, billingCycle);

      // Call the revise subscription function
      const result = await revisePaypalSubscription(currentSubscriptionId, newPlanId);

      if (result.approve_url) {
        // If approval is needed, redirect to PayPal
        window.location.href = result.approve_url;
      } else {
        // Plan changed successfully
        toast({
          title: t('common.success'),
          description: t('subscription.planUpdated'),
        });

        // Refresh the page to show updated subscription
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error: any) {
      console.error('Error changing plan:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('subscription.changePlanError'),
        variant: "destructive",
      });
    } finally {
      setChangingPlan(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{t('subscription.subscriptionPlans')}</h2>
        <p className="text-gray-600">{t('subscription.chooseBestPlan')}</p>

        {/* Payment method info - adapts based on platform */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950 dark:border-blue-800">
          {isNative ? (
            <>
              <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                {paymentMethod === 'apple' ? '🍎' : '🤖'} {t('subscription.securePayment', { defaultValue: 'Secure Payment' })}
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                {paymentMethod === 'apple'
                  ? t('subscription.applePayDescription', { defaultValue: 'Pay securely with Apple In-App Purchase. Your subscription will be managed through your Apple ID.' })
                  : t('subscription.googlePayDescription', { defaultValue: 'Pay securely with Google Play. Your subscription will be managed through your Google account.' })
                }
              </p>
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p className="font-medium text-green-700 dark:text-green-400">📦 {t('subscription.storageCapacityNote')}</p>
              </div>
            </>
          ) : (
            <>
              <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">💳 {t('subscription.securePaymentWithPayPal')}</h3>
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
                {t('subscription.paypalSecureDescription')}
              </p>
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                <p>• <strong>PayPal:</strong> {t('subscription.paypalInternationalDescription')}</p>
                <p className="font-medium text-green-700 dark:text-green-400">💰 {t('subscription.paypalCurrencyConversion')}</p>
                <p className="font-medium text-green-700 dark:text-green-400">📦 {t('subscription.storageCapacityNote')}</p>
              </div>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="annual" onValueChange={(value) => setBillingCycle(value as 'monthly' | 'annual')} className="w-full">
        <div className="flex justify-center mb-6">
          {/* ✅ Fixed responsive design to prevent button overlap in all languages */}
          <TabsList className="bg-gray-100 p-1 rounded-lg w-full max-w-2xl">
            <div className="grid w-full grid-cols-2 gap-2 md:gap-3">
              <TabsTrigger
                value="monthly"
                className="px-3 py-3 rounded-md font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 hover:text-gray-900 text-sm min-h-[3rem] flex items-center justify-center"
              >
                <span className="text-center leading-tight">{t('landing.monthlyBilling')}</span>
              </TabsTrigger>
              <TabsTrigger
                value="annual"
                className="relative px-3 py-3 pr-8 rounded-md font-medium transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 hover:text-gray-900 text-sm min-h-[3rem] flex items-center justify-center"
              >
                <span className="text-center leading-tight">{t('landing.annualBilling')}</span>
                <span className="absolute -top-1 -right-1 text-xs px-2 py-0.5 bg-badge-green-dark text-white rounded-full font-semibold whitespace-nowrap">
                  {t('subscription.save16Percent')}
                </span>
              </TabsTrigger>
            </div>
          </TabsList>
        </div>

        <TabsContent value="monthly" className="mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {renderPricingCards('monthly')}
          </div>
        </TabsContent>

        <TabsContent value="annual" className="mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {renderPricingCards('annual')}
          </div>
        </TabsContent>
      </Tabs>

      {/* ✅ Updated subscription notes with complete translations */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-sm">
        <p className="font-medium mb-2">{t('subscription.subscriptionNotes')}</p>
        <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
          <li>{t('subscription.allPlansInclude')}</li>
          <li><strong>{t('subscription.storageCapacityNote')}</strong></li>
          <li>{t('subscription.storageQuotaNote')}</li>
          <li>{t('subscription.cancelAnytime')}</li>
          <li>{t('subscription.premiumFeaturesImmediate')}</li>
          <li>{t('subscription.annualSubscriptions')}</li>
          <li>{t('subscription.supportContact')}</li>
          <li><strong>{t('subscription.paymentMethodNote')}</strong></li>
        </ul>
      </div>
    </div>
  );

  function renderPricingCards(cycle: 'monthly' | 'annual') {
    return tiers.map((tier) => {
      const isCurrentTier = subscription?.data?.currentTier === tier;
      const features = getTierFeatures(tier, t);
      const price = getTierPrice(tier, cycle, isNative); // Pass isNative for dynamic pricing
      const isPremium = tier === "premium";
      const hasActiveSubscription = subscription?.data?.paypal_subscription_id;

      // Determine tier order for upgrade/downgrade logic
      const tierOrder = { free: 0, standard: 1, premium: 2, ultimate: 3 };
      const currentTierOrder = tierOrder[subscription?.data?.currentTier || 'free'];
      const targetTierOrder = tierOrder[tier];
      const isUpgrade = targetTierOrder > currentTierOrder;
      const isDowngrade = targetTierOrder < currentTierOrder;

      // Use simple border styling - gradient border for all except current tier
      const borderClass = isCurrentTier
        ? "border-primary border-2"
        : "border-2 border-transparent bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-border";

      return (
        <Card key={`${tier}-${cycle}`} className={`${borderClass} ${isPremium ? "relative" : ""}`} style={!isCurrentTier ? {
          background: 'linear-gradient(white, white) padding-box, linear-gradient(to right, #3b82f6, #9333ea) border-box'
        } : {}}>
          {isPremium && (
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs py-2 text-center font-bold rounded-t-lg">
              {t('landing.mostPopular')}
            </div>
          )}
          <CardHeader className={isPremium ? "pt-10" : ""}>
            <CardTitle className="capitalize text-lg">{t(`plans.${tier}.name`)}</CardTitle>
            <CardDescription className="text-xl font-bold text-gray-900">{price}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {features.map((feature: string, index: number) => {
                // Highlight storage capacity numbers, trial days, and PetMatch™
                const highlightedFeature = feature
                  .replace(/(\d+\s*(?:MB|GB))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>')
                  .replace(/(15(?:-day|天|日間| días| jours| giorni| dias))/gi, '<span class="font-bold text-pet-purple text-base">$1</span>')
                  .replace(/(PetMatch™)/gi, '<span class="font-bold text-pet-purple text-base">$1</span>');

                return (
                  <li key={index} className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: highlightedFeature }} />
                  </li>
                );
              })}
            </ul>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            {tier !== "free" && !isCurrentTier && (
              <>
                {hasActiveSubscription ? (
                  <Button
                    onClick={() => handleChangePlan(tier as 'standard' | 'premium' | 'ultimate')}
                    disabled={changingPlan}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                  >
                    {changingPlan ? (
                      t('common.processing')
                    ) : (
                      <>
                        {isUpgrade ? (
                          <><ArrowUpCircle className="w-4 h-4 mr-2" /> {t('subscription.upgradeTo', { tier: t(`plans.${tier}.name`) })}</>
                        ) : isDowngrade ? (
                          <><ArrowDownCircle className="w-4 h-4 mr-2" /> {t('subscription.downgradeTo', { tier: t(`plans.${tier}.name`) })}</>
                        ) : (
                          t('subscription.changeTo', { tier: t(`plans.${tier}.name`) })
                        )}
                      </>
                    )}
                  </Button>
                ) : (
                  <PlatformAwarePaymentButton
                    tier={tier as 'standard' | 'premium' | 'ultimate'}
                    billingCycle={cycle}
                    onSuccess={() => {
                      // Invalidate queries to refresh data instead of full page reload
                      queryClient.invalidateQueries({ queryKey: ['subscription'] });
                      queryClient.invalidateQueries({ queryKey: ['quota'] });

                      // Navigate to success page
                      navigate(`/subscription-success?plan=${tier}&reference=native_purchase`);
                    }}
                    onError={(error) => {
                      toast({
                        title: t('subscription.purchaseError', { defaultValue: 'Purchase failed' }),
                        description: error.message,
                        variant: "destructive",
                      });
                    }}
                  />
                )}
              </>
            )}

            {tier === "free" && (
              <Button disabled={isCurrentTier} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white">
                {isCurrentTier ? t('subscription.currentPlan') : t('subscription.freeTier')}
              </Button>
            )}

            {isCurrentTier && tier !== "free" && (
              <Button disabled className="w-full" variant="outline">
                {t('subscription.currentPlan')}
              </Button>
            )}
          </CardFooter>
        </Card>
      );
    });
  }
};
